import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { PaymentOptionSubmittedProperties } from '@iwoca/frontend-tracking-library/lib/ampli';
import { IconLockCloseOutline, IconPound } from '@iwoca/icons';
import { CheckBox, Modal, Radio } from '@iwoca/orion';
import classnames from 'classnames';
import { set } from 'lodash-es';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import styles from './PaymentDetails.module.css';
import { TrustPilotBadge } from './TrustPilotBadge/TrustPilotBadge';
import { UpdateTurnoverModal } from './UpdateTurnoverModal/UpdateTurnoverModal';
import { iwocapayTelDisplay } from '../../../../src/constants.json';
import { getPrimaryEmailAddressForCustomerState } from '../../../../src/Seller/AccessUsers/AccessUsers';
import {
  useGetSellerByHandle,
  useGetStateByStateKey,
} from '../../../api/lending/lapiHooks';
import { formatCurrency } from '../../../components/Input/formatCurrency';
import { HookFormInput } from '../../../components/Input/Input';
import { displayCurrency } from '../../../utils/DisplayCurrency';
import { getOrdinalDateFromNow } from '../../../utils/DisplayOrdinalDate';
import logger from '../../../utils/logger';
import { Button } from '../../components/Button/Button';
import {
  EmailFormInput,
  useEmailValidation,
} from '../../components/EmailInput/EmailInput';
import {
  WhiteLabelPayLinkLogo,
  PoweredByIwocaPay,
} from '../../components/WhiteLabelLogo/WhiteLabelLogo';
import { WhiteLabelOverride } from '../../components/WhiteLabelOverride/WhiteLabelOverride';
import { TEnabledOptions } from '../../hooks/useEnabledTerms';
import { useWhiteLabelTheme } from '../../store/WhiteLabelProvider';
import { useCurrentAmount } from '../hooks/useCurrentAmount';
import { useGetANTFormalOffers } from '../hooks/useGetANTFormalOffers';
import { useHasInsufficientCredit } from '../hooks/useHasInsufficientCredit';
import { useRepaymentSchedule } from '../hooks/useRepaymentSchedule';
import { useUpdateTurnover } from '../hooks/useUpdateTurnover';
import {
  FormValues,
  TBuyerInterest,
  TDuration,
  TPricingOption,
  TPricingPromotions,
  TProductInterest,
} from '../utils/PayLinkLanding.types';

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 250000;
const MAX_REFERENCE_LENGTH = 50;
export const MAX_AMOUNT_PAY_LATER = 30000;
export const MIN_AMOUNT_PAY_LATER = 150;

const PRICING_OPTION_TO_TRACKING = {
  payNow: 'pay_now',
  payLater1: 'pay_in_1',
  payLater3: 'pay_in_3',
  payLater12: 'pay_in_12',
} as const satisfies Record<
  TPricingOption,
  PaymentOptionSubmittedProperties['payment_option']
>;

export const PaymentDetails = ({
  isPreview = false,
  sellerName,
  pricingPromotions,
  buyerInterest,
  onSubmit,
  initialValues,
  setUrlState,
  enabledOptions = ['THREE_MONTHS', 'TWELVE_MONTHS', 'PAY_NOW'],
  payLinkType,
  lockAmount = false,
  lockReference = false,
}: {
  isPreview: boolean;
  sellerName: string;
  pricingPromotions: TPricingPromotions;
  buyerInterest: TBuyerInterest;
  onSubmit?: ({
    values,
    stateKey,
  }: {
    values: FormValues;
    stateKey?: string;
  }) => Promise<void>;
  initialValues?: {
    amount?: string;
    reference?: string;
  };
  lockAmount: boolean;
  lockReference: boolean;
  setUrlState?: (key: string, value: string) => void;
  enabledOptions: TEnabledOptions;
  payLinkType: 'custom' | 'generic' | 'package' | 'order' | 'preview';
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { state: customerState } = useGetStateByStateKey();
  const { offers: antOffers } = useGetANTFormalOffers();
  const coreANTOffers = antOffers.filter(
    (offer) => offer?.product_type !== 'iwocapay',
  );
  const [showSuperseedModal, setShowSuperseedModal] = useState<boolean>(false);

  const [isTurnoverModalOpen, setIsTurnoverModalOpen] =
    useState<boolean>(false);

  const { shouldUpdateTurnover, previousTurnover } = useUpdateTurnover();

  const validateAmount = (amount?: FormValues['amount']) => {
    if (!amount || isNaN(parseFloat(amount))) return 'Please enter an amount';

    if (parseFloat(amount) > MAX_AMOUNT)
      return 'Please enter an amount up to £250,000.00';

    if (parseFloat(amount) < MIN_AMOUNT)
      return 'Please enter an amount from £1.00';

    return undefined;
  };

  const validateReference = (reference?: FormValues['reference']) => {
    if (!reference) {
      return 'Please enter a reference';
    } else if (reference.length > MAX_REFERENCE_LENGTH) {
      return 'Please enter a reference up to 50 characters';
    }

    return undefined;
  };

  const validatePayNowFullName = async (
    fullName?: FormValues['payNowFullName'],
  ) => {
    const twoOrMoreWordsRegex = /\w+\s+\w+/;
    const fullNameRegex = /^[\p{L}\p{M}\s\-'.]+$/u;
    const fullNameSchema = Yup.string()
      .required('Full name is required')
      .matches(twoOrMoreWordsRegex, 'Full name must contain at least two words')
      .matches(fullNameRegex, 'Full name contains invalid characters');

    try {
      await fullNameSchema.validate(fullName);
      return undefined;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        return error.errors[0];
      }

      return 'Full name is invalid';
    }
  };

  const validatePayNowEmailAddress = async (
    emailAddress?: FormValues['payNowEmailAddress'],
  ) => {
    const emailAddressSchema = Yup.string()
      .email('Email address is invalid')
      .required('Email address is required');

    try {
      await emailAddressSchema.validate(emailAddress);
      return undefined;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        return error.errors[0];
      }

      return 'Email address is invalid';
    }
  };

  const validateEmail = useEmailValidation();

  const validate = async (values: FormValues) => {
    const errors: { [k: string]: string } = {};

    const referenceError = validateReference(values.reference);
    if (referenceError) errors.reference = referenceError;

    const amountError = validateAmount(values.amount);
    if (amountError) errors.amount = amountError;

    if (!values.paymentOption) {
      errors.paymentOption = 'Select an option to continue';
    }

    if (values.paymentOption?.includes('payLater')) {
      if (
        parseFloat(values.amount) > MAX_AMOUNT_PAY_LATER ||
        parseFloat(values.amount) < MIN_AMOUNT_PAY_LATER
      ) {
        const minAmountText = displayCurrency(MIN_AMOUNT_PAY_LATER, 0);
        const maxAmountText = displayCurrency(MAX_AMOUNT_PAY_LATER, 0);

        errors.paymentOption = `Sorry! Pay later is only available for amounts from ${minAmountText} up to ${maxAmountText}`;
      }

      const { errors: emailErrors } = await validateEmail(values);

      if ('emailAddress' in emailErrors) {
        errors.emailAddress = emailErrors.emailAddress!;
      }
    }

    if (values.paymentOption?.includes('payNow')) {
      const payNowFullNameError = await validatePayNowFullName(
        values.payNowFullName,
      );
      if (payNowFullNameError) errors.payNowFullName = payNowFullNameError;

      const payNowEmailAddressError = await validatePayNowEmailAddress(
        values.payNowEmailAddress,
      );
      if (payNowEmailAddressError)
        errors.payNowEmailAddress = payNowEmailAddressError;
    }

    if (Object.keys(errors).length > 0) {
      return {
        values: {},
        errors,
      };
    }

    return {
      values,
      errors: {},
    };
  };

  const initialAmount = initialValues?.amount || '';
  const isInitialAmountValid =
    validateAmount(initialValues?.amount) === undefined;

  const initialReference = initialValues?.reference || '';
  const isInitialReferenceValid =
    validateReference(initialReference) === undefined;

  const initialTurnover = previousTurnover
    ? String(previousTurnover).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : '';

  const initialFormValues: FormValues = {
    reference: isInitialReferenceValid ? initialReference : '',
    amount: isInitialAmountValid ? initialAmount : '',
    paymentOption: null,
    emailAddress: '',
    marketingOptIn: true,
    payNowFullName: '',
    payNowEmailAddress: '',
    payNowMarketingConsent: false,
    turnover: initialTurnover,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialFormValues,
    resolver: validate,
    mode: 'onChange',
  });

  useEffect(() => {
    // Sync to external state of initialFormValues
    // Allows form to reinitialise on change
    methods.reset(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReference, initialAmount, initialTurnover]);

  const submitForm = async ({
    values,
    acceptedSuperseed = false,
    updatedTurnover = false,
  }: {
    values: FormValues;
    acceptedSuperseed?: boolean;
    updatedTurnover?: boolean;
  }) => {
    if (!onSubmit) return;

    if (coreANTOffers.length > 0 && !acceptedSuperseed) {
      return setShowSuperseedModal(true);
    }

    if (shouldUpdateTurnover && !updatedTurnover) {
      return setIsTurnoverModalOpen(true);
    }

    try {
      await onSubmit({
        values,
        stateKey: customerState?.state_key,
      });

      if (payLinkType !== 'preview') {
        tracking.ampli.paymentOptionSubmitted({
          payment_option: PRICING_OPTION_TO_TRACKING[values.paymentOption!],
          // @ts-expect-error Can also be of type "order", library needs update
          pay_link_type: payLinkType,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logger.error(error.message, error);

      try {
        const errorMessage = JSON.parse(error.message);
        if (
          errorMessage.errors.some(
            (error: { code: string; detail: string }) =>
              error.detail ===
              'Sole trader cannot use pay later with non-ST seller.',
          )
        ) {
          return setSubmitError(
            'Pay later is not available for Sole Traders on this transaction',
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return setSubmitError(
          `Oops something went wrong. Please try again or give us a call on ${iwocapayTelDisplay}.`,
        );
      }

      return setSubmitError(
        `Oops something went wrong. Please try again or give us a call on ${iwocapayTelDisplay}.`,
      );
    }
  };
  const onHookFormSubmit = methods.handleSubmit((values) =>
    submitForm({ values }),
  );

  const SuperseedAcceptance = () => {
    const acceptConditions = async () => {
      setShowSuperseedModal(false);
      await submitForm({
        values: methods.getValues(),
        acceptedSuperseed: true,
      });
    };

    return (
      <Modal
        isOpen={showSuperseedModal}
        onClose={() => setShowSuperseedModal(false)}
      >
        <h1 className={styles.creditFacilityWarningTitle}>
          Once you hit continue we’ll need to re-run your existing Flexi-Loan
          offer for £
          {formatCurrency(`${coreANTOffers[0]?.available_credit || 0}`)}
        </h1>
        <p className={styles.creditFacilityWarningText}>
          Don’t worry &mdash; you won’t need to give us any more documents to do
          this. To see your Flexi-Loan offer, go to your iwoca dashboard.
        </p>
        <div className={styles.creditFacilityButtonContainer}>
          <Button onClick={acceptConditions}>Continue with iwocaPay</Button>
          <Button
            onClick={() => setShowSuperseedModal(false)}
            variant="tertiary"
          >
            Go back
          </Button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={onHookFormSubmit} noValidate>
          <SuperseedAcceptance />
          <UpdateTurnoverModal
            isOpen={isTurnoverModalOpen}
            onClose={() => setIsTurnoverModalOpen(false)}
            submitForm={submitForm}
          />
          <PaymentDetailsForm
            isPreview={isPreview}
            sellerName={sellerName}
            pricingPromotions={pricingPromotions}
            buyerInterest={buyerInterest}
            enabledOptions={enabledOptions}
            submitError={submitError}
            setUrlState={setUrlState}
            onOptionChange={(paymentOption) => {
              if (payLinkType !== 'preview') {
                tracking.ampli.paymentOptionSelected({
                  payment_option: PRICING_OPTION_TO_TRACKING[paymentOption],
                  // @ts-expect-error Can also be of type "order", library needs update
                  pay_link_type: payLinkType,
                });
              }
            }}
            lockAmount={lockAmount}
            lockReference={lockReference}
          />
        </form>
      </FormProvider>
    </>
  );
};

const PAYMENT_OPTION_INPUT_ID = 'paymentOption';

const PaymentOptionField = ({
  value,
  label,
  onChange,
}: {
  isPreview: boolean;
  value: TPricingOption;
  label: ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const id = `${PAYMENT_OPTION_INPUT_ID}-${value}`;
  const { register, watch, setValue } = useFormContext();
  const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);

  const { hasInsufficientCredit } = useHasInsufficientCredit({
    pricingOption: value,
  });

  const isChecked = useMemo(
    () => watchPaymentOption === value && !hasInsufficientCredit,
    [hasInsufficientCredit, value, watchPaymentOption],
  );

  useEffect(() => {
    if (hasInsufficientCredit && watchPaymentOption === value)
      setValue(PAYMENT_OPTION_INPUT_ID, null);
  }, [hasInsufficientCredit, setValue, watchPaymentOption, value]);

  return (
    <section
      className={classnames(styles.paymentOption, {
        [styles.paymentOptionSelected]: isChecked,
        [styles.disabled]: hasInsufficientCredit,
      })}
    >
      <label className={styles.paymentOptionHeader} htmlFor={id}>
        <div className={styles.paymentOptionsLabelWrapper}>
          <Radio
            {...register(PAYMENT_OPTION_INPUT_ID, {
              onChange: (e) => {
                onChange?.(e);
              },
            })}
            id={id}
            checked={isChecked}
            value={value}
            className={styles.paymentOptionsRadio}
            disabled={hasInsufficientCredit}
          />
          <div>
            <label className={styles.paymentOptionsLabel} htmlFor={id}>
              {label}
            </label>
            {hasInsufficientCredit && (
              <div className={styles.creditWarning}>
                Not enough limit available
              </div>
            )}
          </div>
        </div>
      </label>
    </section>
  );
};

const PaymentDetailsForm = ({
  isPreview = false,
  sellerName,
  pricingPromotions,
  buyerInterest,
  submitError,
  setUrlState,
  onOptionChange,
  enabledOptions = ['THREE_MONTHS', 'TWELVE_MONTHS', 'PAY_NOW'],
  lockAmount = false,
  lockReference = false,
}: {
  isPreview: boolean;
  sellerName: string;
  pricingPromotions: TPricingPromotions;
  buyerInterest: TBuyerInterest;
  submitError: string | null;
  setUrlState?: (key: string, value: string) => void;
  onOptionChange?: (paymentOption: TPricingOption) => void;
  enabledOptions: TEnabledOptions;
  lockAmount: boolean;
  lockReference: boolean;
}) => {
  const { state: customerState } = useGetStateByStateKey();
  const loggedInCustomerEmailAddress =
    getPrimaryEmailAddressForCustomerState(customerState);

  const {
    formState,
    setValue: setFieldValue,
    trigger,
    watch,
    clearErrors,
  } = useFormContext<FormValues>();
  const initialValues = formState.defaultValues!;
  const errors = formState.errors;
  const paymentOption = watch('paymentOption');

  const { showSellerLogo, theme } = useWhiteLabelTheme();

  useEffect(() => {
    setFieldValue('emailAddress', loggedInCustomerEmailAddress || '');
  }, [loggedInCustomerEmailAddress, setFieldValue, trigger]);

  const isValidPrefillAmount = (amount?: number) => {
    if (!amount) return false;

    return amount >= MIN_AMOUNT && amount <= MAX_AMOUNT;
  };

  const isValidPrefillReference = (reference?: string) => {
    if (!reference) return false;

    return reference.length <= MAX_REFERENCE_LENGTH;
  };

  const disableAmount =
    lockAmount &&
    isValidPrefillAmount(
      initialValues.amount ? parseFloat(initialValues.amount) : undefined,
    );
  const disableReference =
    lockReference &&
    isValidPrefillReference(
      initialValues?.reference ? initialValues.reference : undefined,
    );

  const isFormPrefilled = disableAmount && disableReference;

  return (
    <>
      <div className={styles.paymentDetailsContainer}>
        <div className="mb-l flex items-center justify-start text-s font-med">
          <IconLockCloseOutline className="mr-xs fill-success-drk" />
          <span className="text-success-drk">100% Secure checkout</span>
        </div>
        {showSellerLogo && (
          <WhiteLabelPayLinkLogo
            // showSellerLogo = true --> logo_uri & preferred_logo_type present.
            url={theme.logo_uri!}
            ratio={theme.preferred_logo_type!}
            themeColor={theme.primary_color}
          />
        )}
        <div>
          <h1 className="m-0 text-2xl">Your payment to {sellerName}</h1>
          {showSellerLogo ? <div className="mb-l" /> : <PoweredByIwocaPay />}
        </div>

        {isFormPrefilled ? (
          <div className="flex gap-l bg-[#F4F7FA] p-l text-s">
            <div>
              Order amount:{' '}
              <span className="font-med">
                {displayCurrency(parseFloat(initialValues.amount!))}
              </span>
            </div>
            <div>
              Order reference:{' '}
              <span className="font-med">{initialValues.reference}</span>
            </div>
          </div>
        ) : (
          <>
            <div
              className={classnames(
                styles.paymentDetailsReferenceAmountContainer,
                styles.paymentDetailsLabel,
              )}
            >
              Enter a reference and amount
            </div>
            <div className={styles.paymentDetailsFields}>
              <div className={styles.fieldContainer}>
                <div className={styles.amountFieldContainer}>
                  <HookFormInput
                    label="Amount"
                    type="number"
                    min="1"
                    step="0.01"
                    max="250000"
                    name="amount"
                    id="paymentDetailsAmount"
                    iconLeft={<IconPound />}
                    onChange={(ev) => setUrlState?.('amount', ev.target.value)}
                    disabled={disableAmount}
                  />
                </div>
              </div>
              <div className={styles.fieldContainer}>
                <HookFormInput
                  label="Reference"
                  type="text"
                  name="reference"
                  placeholder="Invoice reference"
                  id="paymentDetailsReference"
                  onChange={(ev) => setUrlState?.('reference', ev.target.value)}
                  disabled={disableReference}
                />
              </div>
            </div>
          </>
        )}

        <>
          <div className="pb-m pt-xl">
            <div className="text-m font-med text-secondary-20">
              Choose a payment option
            </div>
          </div>
        </>

        <div className={styles.paymentOptionsContainer}>
          {enabledOptions.includes('PAY_NOW') && (
            <PaymentOptionField
              isPreview={isPreview}
              value="payNow"
              label={<PayNowRadioOptionLabel />}
              onChange={(e) => {
                onOptionChange?.(e.target.value as TPricingOption);
              }}
            />
          )}
          {enabledOptions.includes('THIRTY_DAYS') &&
            buyerInterest.THIRTY_DAYS && (
              <PayLaterOption
                isPreview={isPreview}
                pricingPromotions={pricingPromotions.THIRTY_DAYS!}
                productInterest={buyerInterest.THIRTY_DAYS}
                duration={1}
                onChange={(e) => {
                  clearErrors();
                  onOptionChange?.(e.target.value as TPricingOption);
                }}
              />
            )}
          {enabledOptions.includes('THREE_MONTHS') && (
            <PayLaterOption
              isPreview={isPreview}
              pricingPromotions={pricingPromotions.THREE_MONTHS}
              productInterest={buyerInterest.THREE_MONTHS}
              duration={3}
              onChange={(e) => {
                clearErrors();
                onOptionChange?.(e.target.value as TPricingOption);
              }}
            />
          )}
          {enabledOptions.includes('TWELVE_MONTHS') &&
            buyerInterest.TWELVE_MONTHS && (
              <PayLaterOption
                isPreview={isPreview}
                pricingPromotions={pricingPromotions.TWELVE_MONTHS!}
                productInterest={buyerInterest.TWELVE_MONTHS}
                duration={12}
                onChange={(e) => {
                  clearErrors();
                  onOptionChange?.(e.target.value as TPricingOption);
                }}
              />
            )}
          {errors.paymentOption && (
            <div className="text-s font-med text-error">
              {errors.paymentOption as unknown as string}
            </div>
          )}
        </div>

        {!paymentOption && (
          <CheckoutButton
            isDisabled={!paymentOption}
            buttonText={
              !isPreview && Boolean(loggedInCustomerEmailAddress)
                ? 'Finish checkout'
                : 'Create account and check out'
            }
          />
        )}
        {paymentOption === 'payNow' && <PayNowSelected />}
        {paymentOption?.includes('payLater') && (
          <EmailFormInput loggedIn={Boolean(loggedInCustomerEmailAddress)} />
        )}
        {showSellerLogo && (
          <div className="flex justify-center">
            <PoweredByIwocaPay />
          </div>
        )}
        {submitError && <p className={styles.fieldError}>{submitError}</p>}
        <TrustPilotBadge />
      </div>
    </>
  );
};

const PayNowRadioOptionLabel = () => {
  const amount = useCurrentAmount();
  const displayAmount = amount && displayCurrency(parseFloat(amount));

  return (
    <div className={styles.paymentOptionLabel}>
      {amount ? (
        <p>
          <b> Pay {displayAmount} </b> with an instant bank transfer
        </p>
      ) : (
        <p>
          <b> Pay Now </b> with an instant bank transfer
        </p>
      )}
    </div>
  );
};

function checkIsEligibleForPayLater(amount: number) {
  if (!amount) return true;

  if (amount >= MIN_AMOUNT_PAY_LATER && amount <= MAX_AMOUNT_PAY_LATER)
    return true;

  return false;
}

const PayLaterOption = ({
  isPreview = false,
  pricingPromotions,
  productInterest,
  onChange,
  duration,
}: {
  isPreview: boolean;
  pricingPromotions: string[];
  productInterest: TProductInterest;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  duration: TDuration;
}) => {
  const { sellerHandle } = useParams();
  const { seller } = useGetSellerByHandle(sellerHandle);
  const amount = useCurrentAmount();
  const debouncedAmount = useDebounce(amount, 500);
  const { watch } = useFormContext();
  const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);
  const isChecked = watchPaymentOption === `payLater${duration}`;

  const { exampleMonthlyRepayment } = useRepaymentSchedule({
    amount: debouncedAmount,
    representativeInterest: productInterest.isSellerPays
      ? 0
      : productInterest.rate,
    duration,
    pricingPromotions,
  });

  const isEligibleForPayLater = checkIsEligibleForPayLater(
    parseInt(amount || ''),
  );

  if (!isEligibleForPayLater) {
    return (
      <PaymentOptionField
        isPreview={isPreview}
        value={`payLater${duration}`}
        label={
          <PayLaterRadioOptionLabel
            exampleMonthlyRepayment={exampleMonthlyRepayment}
            productInterest={productInterest}
            duration={duration}
          />
        }
        onChange={onChange}
      />
    );
  }

  return (
    <PaymentOptionField
      isPreview={isPreview}
      value={`payLater${duration}`}
      label={
        <>
          <PayLaterRadioOptionLabel
            exampleMonthlyRepayment={exampleMonthlyRepayment}
            productInterest={productInterest}
            duration={duration}
          />
          {!seller?.capture_delay_hours && (
            <p className="text-s text-structure-40">
              Nothing to pay until {getOrdinalDateFromNow(30)}
            </p>
          )}
          {!!seller?.capture_delay_hours && isChecked && (
            <CapturedPaymentNotice />
          )}
        </>
      }
      onChange={onChange}
    />
  );
};

const PayLaterRadioOptionLabel = ({
  exampleMonthlyRepayment,
  productInterest,
  duration,
}: {
  exampleMonthlyRepayment?: string;
  productInterest: TProductInterest;
  duration: TDuration;
}) => {
  const amount = useCurrentAmount();
  const isInterestFree = productInterest.isSellerPays;

  const interestType = () => {
    if (isInterestFree) {
      return 'interest-free';
    }
    return `${(productInterest.rate * 100).toFixed(2)}% per month ${productInterest.type === 'representative' ? 'rep.' : ''}`;
  };

  return (
    <div className={styles.paymentOptionLabel}>
      <PayLaterRadioOptionLabelText
        exampleMonthlyRepayment={exampleMonthlyRepayment}
        amount={amount}
        duration={duration}
      />
      <p className={styles.paymentOptionLabelInterestTypeSeperator}>|</p>
      <p
        className={classnames(styles.paymentOptionLabelInterestType, {
          [styles.interestFree]: isInterestFree,
        })}
      >
        {interestType()}
      </p>
    </div>
  );
};

const PayLaterRadioOptionLabelText = ({
  exampleMonthlyRepayment,
  amount,
  duration,
}: {
  exampleMonthlyRepayment?: string;
  amount?: string;
  duration: TDuration;
}) => {
  if (!amount) {
    if (duration === 1) {
      return (
        <p>
          <b> Pay in 30 days time </b>
        </p>
      );
    }
    return (
      <p>
        <b> Pay Later </b> for {duration} months
      </p>
    );
  }

  if (parseFloat(amount) < MIN_AMOUNT_PAY_LATER) {
    if (duration === 1) {
      return (
        <p>
          <b> Pay in 30 days </b> isn't available for under{' '}
          {displayCurrency(MIN_AMOUNT_PAY_LATER, 0)}
        </p>
      );
    }
    return (
      <p>
        <b> Pay over {duration} months </b> isn't available for under{' '}
        {displayCurrency(MIN_AMOUNT_PAY_LATER, 0)}
      </p>
    );
  }

  if (parseFloat(amount) > MAX_AMOUNT_PAY_LATER) {
    if (duration === 1) {
      return (
        <p>
          <b> Pay in 30 days </b> isn't available for over{' '}
          {displayCurrency(MAX_AMOUNT_PAY_LATER, 0)}
        </p>
      );
    }
    return (
      <p>
        <b> Pay over {duration} months </b> isn't available for over{' '}
        {displayCurrency(MAX_AMOUNT_PAY_LATER, 0)}
      </p>
    );
  }

  if (!exampleMonthlyRepayment) {
    if (duration === 1) {
      return (
        <p>
          <b> Pay in 30 days time </b>
        </p>
      );
    }
    return (
      <p>
        <b> Pay Later </b> for {duration} months
      </p>
    );
  }

  if (duration === 1) {
    return (
      <p>
        <b> Pay {exampleMonthlyRepayment} </b> in 30 days time
      </p>
    );
  }
  return (
    <p>
      <b> Pay {exampleMonthlyRepayment} </b> per month for {duration} months
    </p>
  );
};

const CapturedPaymentNotice = () => {
  const { sellerHandle } = useParams();
  const { seller, loadingSeller } = useGetSellerByHandle(sellerHandle);

  if (loadingSeller || !seller || !seller.capture_delay_hours) return null;

  return (
    <div className={styles.capturedPaymentDescription}>
      <p className={styles.capturedPaymentSubheading}>
        Your payment schedule will begin once {seller.trading_name} has
        confirmed the order.{' '}
        <span className={styles.capturedPaymentSubheadingBold}>
          Your first payment will be 30 days after that date.
        </span>
      </p>
    </div>
  );
};

export const CheckoutButton = ({
  buttonText,
  isDisabled = false,
  isLoading = false,
  ...props
}: {
  buttonText: string;
  isDisabled: boolean;
  isLoading?: boolean;
}) => {
  return (
    <div>
      <WhiteLabelOverride componentType="button" disabled={isDisabled}>
        <Button
          type="submit"
          className="mt-l w-full"
          disabled={isDisabled}
          loading={isLoading}
          {...props}
        >
          {buttonText}
        </Button>
      </WhiteLabelOverride>
    </div>
  );
};

const PayNowSelected = () => {
  const { formState, trigger, register } = useFormContext();
  const { errors, touchedFields } = formState;
  const isSubmitting = formState.isSubmitting;
  const FULL_NAME_FIELD_ID = 'payNowFullName';
  const EMAIL_ADDRESS_FIELD_ID = 'payNowEmailAddress';
  const MARKETING_CONSENT_FIELD_ID = 'payNowMarketingConsent';

  const fieldsHaveBeenTouched =
    formState.touchedFields[FULL_NAME_FIELD_ID] &&
    formState.touchedFields[EMAIL_ADDRESS_FIELD_ID];

  const isSubmitDisabled =
    isSubmitting ||
    Object.keys(formState.errors).length !== 0 ||
    !fieldsHaveBeenTouched;

  return (
    <div>
      <div className="mb-l grid grid-cols-2 gap-l pt-l sm:grid-cols-1">
        <div>
          <HookFormInput
            label="Full name"
            type="text"
            name="payNowFullName"
            placeholder="Joe Bloggs"
            id={FULL_NAME_FIELD_ID}
            onChange={async () => {
              set(formState.touchedFields, FULL_NAME_FIELD_ID, true);
              await trigger(FULL_NAME_FIELD_ID);
            }}
            showError={false}
          />
          {touchedFields[FULL_NAME_FIELD_ID] && errors[FULL_NAME_FIELD_ID] && (
            <div className="mt-s text-s text-error">
              {(errors[FULL_NAME_FIELD_ID] as unknown as string) ||
                'Unknown error'}
            </div>
          )}
        </div>
        <div>
          <HookFormInput
            label="Email for receipt"
            type="email"
            name="payNowEmailAddress"
            placeholder="name@email.co.uk"
            id={EMAIL_ADDRESS_FIELD_ID}
            onChange={async () => {
              set(formState.touchedFields, EMAIL_ADDRESS_FIELD_ID, true);
              await trigger(EMAIL_ADDRESS_FIELD_ID);
            }}
            showError={false}
          />
          {touchedFields[EMAIL_ADDRESS_FIELD_ID] &&
            errors[EMAIL_ADDRESS_FIELD_ID] && (
              <div className="mt-s text-s text-error">
                {(errors[EMAIL_ADDRESS_FIELD_ID] as unknown as string) ||
                  'Unknown error'}
              </div>
            )}
        </div>
      </div>
      <CheckBox
        {...register(MARKETING_CONSENT_FIELD_ID)}
        id={MARKETING_CONSENT_FIELD_ID}
        label="Hear more about iwoca"
      />
      <p className="mb-xl mt-xl text-s text-structure-40">
        Email address will be used to send you your payment receipt
      </p>
      <CheckoutButton
        buttonText="Finish checkout"
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitting}
      />
    </div>
  );
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  });
  useEffect(() => {
    const handler = setTimeout(() => {
      if (!isMountedRef) return;
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
